import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { useTheme } from '../contexts/ThemeContext';
import { Edit, Trash2, X } from 'lucide-react';
import CityMultiSelect from '../components/CityMultiSelect';
import citiesData from '../data/cities_with_coordinates.json';
import InviteModal from '../components/InviteModal';
import UserAvatar from '../components/common/UserAvatar';
import CityReportModal from '../components/common/CityReportModal';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

const SettingsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  padding-bottom: 120px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding-bottom: 0;
  }
`;

const Sidebar = styled.aside<{ $hidden?: boolean }>`
  width: 280px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 40px 0;
  position: fixed;
  top: 56px;
  height: calc(100vh - 56px - 120px);
  overflow-y: auto;

  @media (max-width: 768px) {
    display: ${p => p.$hidden ? 'none' : 'flex'};
    flex-direction: column;
    width: 100%;
    position: relative;
    top: auto;
    height: auto;
    min-height: calc(100vh - 56px);
    border-right: none;
    border-bottom: none;
    padding: 0 0 120px 0;
    overflow-y: visible;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 24px 24px 24px;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    padding: 20px 20px 16px 20px;
  }
`;

const SidebarTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const SidebarSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

const SidebarNav = styled.nav`
  padding: 16px 0;
`;

const NavSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const NavSectionTitle = styled.div`
  padding: 8px 24px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;

  @media (max-width: 768px) {
    padding: 14px 20px 8px 20px;
    background: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
    border-top: 1px solid #f1f5f9;
  }
`;

const NavItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: ${props => props.$active ? '#f1f5f9' : 'transparent'};
  border: none;
  border-left: 3px solid ${props => props.$active ? 'var(--accent)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
    flex-shrink: 0;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.$active ? '#1e293b' : '#64748b'};
  }

  &:hover {
    background: #f8fafc;

    svg {
      color: var(--accent);
    }

    span {
      color: #1e293b;
    }
  }

  @media (max-width: 768px) {
    padding: 16px 20px;
    border-left-width: 0;
    border-bottom: 1px solid #f1f5f9;
    background: white;

    svg {
      color: #64748b;
    }

    span {
      color: #1e293b;
      font-size: 15px;
    }

    &::after {
      content: '›';
      margin-left: auto;
      font-size: 22px;
      color: #cbd5e1;
      line-height: 1;
    }

    &:active {
      background: #f1f5f9;
    }
  }
`;

const MainContent = styled.main<{ $hidden?: boolean }>`
  flex: 1;
  margin-left: 280px;
  padding: 40px;
  padding-bottom: 120px;

  @keyframes mobileSlideIn {
    from { transform: translateX(20px); opacity: 0.6; }
    to   { transform: translateX(0);    opacity: 1;   }
  }

  @media (max-width: 768px) {
    display: ${p => p.$hidden ? 'none' : 'flex'};
    flex-direction: column;
    margin-left: 0;
    padding: 0;
    min-height: calc(100vh - 56px);
    animation: mobileSlideIn 0.22s ease-out;
  }
`;

const MobileBackHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
    flex-shrink: 0;
  }
`;

const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  color: var(--accent);
  font-size: 15px;
  font-weight: 600;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &:active { opacity: 0.7; }
`;

const MobileContentArea = styled.div`
  @media (max-width: 768px) {
    flex: 1;
    padding: 20px 16px 100px 16px;
    overflow-y: auto;
    box-sizing: border-box;
  }
`;

const ContentHeader = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 24px 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
    margin: 0 0 16px 0;
  }
`;

const ContentTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const ContentSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;


const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
    color: var(--accent);
  }
`;

const CardButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${props => props.$variant === 'secondary' || props.$variant === 'danger' ? '8px 16px' : '10px 20px'};
  background: ${props =>
    props.$variant === 'secondary' ? 'transparent' :
    props.$variant === 'danger' ? 'transparent' :
    'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)'
  };
  color: ${props =>
    props.$variant === 'secondary' ? '#64748b' :
    props.$variant === 'danger' ? '#ef4444' :
    'white'
  };
  border: ${props =>
    props.$variant === 'secondary' ? '1px solid #e2e8f0' :
    props.$variant === 'danger' ? '1px solid #fecaca' :
    'none'
  };
  border-radius: 8px;
  font-size: ${props => props.$variant === 'secondary' || props.$variant === 'danger' ? '13px' : '14px'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    transform: translateY(-1px);
    ${props => props.$variant === 'secondary' ? 'background: #f1f5f9; border-color: #cbd5e1;' : ''}
    ${props => props.$variant === 'danger' ? 'background: #fef2f2; border-color: #fca5a5;' : ''}
    ${props => !props.$variant || props.$variant === 'primary' ? 'box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.35);' : ''}
  }

  &:active {
    transform: translateY(0);
  }
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const ProfileEmail = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 8px 0;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: rgba(var(--accent-rgb), 0.12);
  color: var(--accent);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
`;

const RegionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RegionInfo = styled.div`
  flex: 1;
`;

const RegionName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const RegionDetails = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const RegionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  padding: 6px;
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    color: #64748b;
  }

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;

    svg {
      color: #1e293b;
    }
  }
`;

const SideSheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
`;

const SideSheetContent = styled.div`
  background: white;
  width: 100%;
  max-width: 540px;
  height: 100vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const SideSheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const SideSheetTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    color: #64748b;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const SideSheetForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
  flex: 1;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const SideSheetButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$variant === 'primary' ? `
    background: var(--accent);
    color: white;

    &:hover:not(:disabled) {
      background: var(--accent-dark);
      transform: translateY(-1px);
    }

    &:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
  ` : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const ErrorMessage = styled.div`
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  padding: 12px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #16a34a;
  font-size: 14px;
`;

// Team Section Styled Components
const TeamTabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
`;

const TeamTab = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  border-bottom: 3px solid ${props => props.active ? 'var(--accent)' : 'transparent'};
  margin-bottom: -2px;

  &:hover {
    color: ${props => props.active ? 'var(--accent)' : '#1e293b'};
  }

  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const MemberCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--accent);
  }
`;

const PhotoWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #e2e8f0;
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MemberName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const MemberRole = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
  margin-bottom: 8px;
`;

const MemberEmail = styled.div`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const InvitesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const InviteCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const InviteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
`;

const InviteInfo = styled.div`
  flex: 1;
`;

const InviteName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
`;

const InviteLevel = styled.div<{ level: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    if (props.level === 'OURO') return '#fef3c7';
    if (props.level === 'PRATA') return '#e5e7eb';
    return '#fed7aa';
  }};
  color: ${props => {
    if (props.level === 'OURO') return '#92400e';
    if (props.level === 'PRATA') return '#374151';
    return '#9a3412';
  }};
`;

const InviteCode = styled.div`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
  text-align: center;
  margin-bottom: 8px;
  letter-spacing: 1px;
`;

const InviteExpiry = styled.div`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TeamEmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: #64748b;
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #475569;
`;

const EmptyStateText = styled.div`
  font-size: 14px;
  color: #94a3b8;
`;

const SearchAndViewContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SearchBar = styled.input`
  flex: 1;
  min-width: 250px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const ViewModeButtons = styled.div`
  display: flex;
  gap: 6px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
`;

const ViewModeButton = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.active ? 'var(--accent)' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: var(--accent);
  }
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MemberListItem = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--accent);
  }
`;

const ListPhotoWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  flex-shrink: 0;
  transition: all 0.2s ease;

  ${MemberListItem}:hover & {
    border-color: var(--accent);
  }
`;

const ListMemberInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ListMemberHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ListMemberName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const ListMemberRole = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--accent);
  background: rgba(var(--accent-rgb), 0.08);
  padding: 3px 10px;
  border-radius: 6px;
`;

const ListMemberEmail = styled.div`
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

interface Regiao {
  id: number;
  nome: string;
  cidade_ids: number[];
  created_at: string;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  email?: string;
  phone?: string;
  tipo_usuario: 'ADMIN' | 'ASSESSOR';
  nivel_acesso: string;
  created_at: string;
  foto_url?: string | null;
}

interface Invite {
  id: number;
  codigo: string;
  nome_assessor: string;
  nivel_assessor: 'BRONZE' | 'PRATA' | 'OURO';
  valido_ate: string;
  usado: boolean;
  criado_em: string;
  criador_nome?: string;
}

type SettingsSection = 'profile' | 'regions' | 'team' | 'reports' | 'branding';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData, signOut, isAdmin, session, refreshUserData, isPasswordRecovery, updatePassword, clearPasswordRecovery } = useAuth();
  const { can } = usePermissionCheck();
  const { logoUrl: currentLogoUrl } = useTheme();
  const canManageRegions = isAdmin || can('regions.manage');
  const canManageUsers = isAdmin || can('users.manage');
  const canAccessReports = isAdmin || can('reports.manage');
  const canManageBranding = isAdmin || can('groups.branding');
  const [activeSection, setActiveSection] = useState<SettingsSection>(() => {
    const s = searchParams.get('section');
    if (s === 'reports' || s === 'regions' || s === 'team' || s === 'profile') return s;
    return 'profile';
  });
  const [regioes, setRegioes] = useState<Regiao[]>([]);
  const [loadingRegioes, setLoadingRegioes] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [nomeRegiao, setNomeRegiao] = useState('');
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState<number[]>([]);
  const [creatingRegiao, setCreatingRegiao] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Team states
  const [teamTab, setTeamTab] = useState<'members' | 'invites'>('members');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [teamViewMode, setTeamViewMode] = useState<'grid' | 'list'>('grid');
  const [mobileView, setMobileView] = useState<'menu' | 'content'>('menu');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Branding state
  const [brandingColor, setBrandingColor] = useState('');
  const [brandingHex, setBrandingHex] = useState('');
  const [brandingLogoFile, setBrandingLogoFile] = useState<File | null>(null);
  const [brandingLogoPreview, setBrandingLogoPreview] = useState<string | null>(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSuccess, setBrandingSuccess] = useState('');
  const [brandingError, setBrandingError] = useState('');

  // Change password / password recovery modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordUpdateError, setPasswordUpdateError] = useState('');
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Auto-open profile section and show modal when coming from password reset link
  useEffect(() => {
    if (isPasswordRecovery) {
      setActiveSection('profile');
      setMobileView('content');
    }
  }, [isPasswordRecovery]);

  const getSectionTitle = () => {
    switch (activeSection) {
      case 'profile': return 'Perfil';
      case 'regions': return 'Regiões Políticas';
      case 'team': return 'Time do Grupo';
      case 'reports': return 'Relatórios';
      case 'branding': return 'Identidade Visual';
      default: return 'Configurações';
    }
  };

  useEffect(() => {
    if (activeSection === 'regions' && canManageRegions) {
      fetchRegioes();
    }
    if (activeSection === 'team' && canManageUsers) {
      fetchTeamMembers();
      fetchInvites();
    }
    if (activeSection === 'branding' && userData) {
      const color = userData.grupo_cor_primaria ?? '';
      setBrandingColor(color);
      setBrandingHex(color);
      setBrandingLogoPreview(userData.grupo_logo_url ?? null);
      setBrandingLogoFile(null);
    }
  }, [activeSection, canManageRegions, canManageUsers]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchRegioes = async () => {
    setLoadingRegioes(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setRegioes(result.data);
        }
      } else {
        console.error('Erro ao buscar regiões:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao buscar regiões:', error);
    } finally {
      setLoadingRegioes(false);
    }
  };

  const handleCreateRegiao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.access_token) return;

    setCreatingRegiao(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: nomeRegiao,
          cidade_ids: cidadesSelecionadas
        })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Erro ao criar região');
        setCreatingRegiao(false);
        return;
      }

      setSuccess('Região criada com sucesso!');
      setNomeRegiao('');
      setCidadesSelecionadas([]);
      setShowCreateModal(false);
      fetchRegioes();
    } catch (err) {
      setError('Erro ao criar região política');
    } finally {
      setCreatingRegiao(false);
    }
  };

  const handleDeleteRegiao = async (regiaoId: number) => {
    if (!session?.access_token) return;
    if (!window.confirm('Tem certeza que deseja deletar esta região?')) return;

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/regioes/${regiaoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        setSuccess('Região deletada com sucesso');
        fetchRegioes();
      } else {
        setError('Erro ao deletar região');
      }
    } catch (err) {
      setError('Erro ao deletar região política');
    }
  };

  const getCityName = (cityId: number): string => {
    const cities = citiesData as any[];
    const city = cities.find(c => c.id === cityId);
    return city ? `${city.nome} - ${city.microrregiao.mesorregiao.UF.sigla}` : `Cidade #${cityId}`;
  };

  // Team functions
  const fetchTeamMembers = async () => {
    if (!session?.access_token) return;
    
    setLoadingTeam(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const mappedMembers: TeamMember[] = result.data.map((user: any) => ({
            id: user.id,
            name: user.nome,
            email: user.email_contato,
            tipo_usuario: user.tipo_usuario,
            nivel_acesso: user.nivel_acesso,
            role: user.tipo_usuario === 'ADMIN' ? 'Administrador' : `Assessor - ${user.nivel_acesso}`,
            created_at: user.created_at,
            foto_url: user.foto_url || null,
          }));
          setTeamMembers(mappedMembers);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar membros do time:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  const fetchInvites = async () => {
    if (!session?.access_token) return;
    
    setLoadingInvites(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/convites`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setInvites(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar convites:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleInviteSuccess = () => {
    fetchInvites();
  };

  const getPhotoUrl = (id: number) => {
    const avatarId = (id % 70) + 1;
    return `https://i.pravatar.cc/300?img=${avatarId}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isExpired = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  const pendingInvites = invites.filter(inv => !inv.usado && !isExpired(inv.valido_ate));

  const filteredTeamMembers = teamMembers.filter(member => 
    member.name.toLowerCase().includes(teamSearchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(teamSearchTerm.toLowerCase()) ||
    (member.email && member.email.toLowerCase().includes(teamSearchTerm.toLowerCase()))
  );

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (tipo?: string) => {
    if (tipo === 'ADMIN') return 'Administrador';
    return 'Assessor';
  };

  const handleClosePasswordModal = () => {
    setShowChangePasswordModal(false);
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordUpdateError('');
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordUpdateError('');

    if (newPassword.length < 6) {
      setPasswordUpdateError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordUpdateError('As senhas não coincidem.');
      return;
    }

    setUpdatingPassword(true);
    const { error } = await updatePassword(newPassword);
    setUpdatingPassword(false);

    if (error) {
      setPasswordUpdateError(error.message);
    } else {
      setPasswordUpdateSuccess(true);
      setNewPassword('');
      setConfirmNewPassword('');
      clearPasswordRecovery();
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSaveBranding = async () => {
    if (!userData?.grupo_politico_id || !session?.access_token) return;
    setBrandingLoading(true);
    setBrandingError('');
    setBrandingSuccess('');
    try {
      const grupoId = userData.grupo_politico_id;

      // 1. Upload logo if a new file was selected
      if (brandingLogoFile) {
        const formData = new FormData();
        formData.append('logo', brandingLogoFile);
        const uploadRes = await apiFetch(`${API_BASE_URL}/api/v1/grupo-politico/${grupoId}/logo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
          body: formData,
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({}));
          throw new Error(err.error || 'Erro ao enviar logo');
        }
      }

      // 2. Save color
      const body: Record<string, any> = {};
      if (brandingHex !== (userData.grupo_cor_primaria ?? '')) {
        if (brandingHex === '' || /^#[0-9A-Fa-f]{6}$/.test(brandingHex)) {
          body.cor_primaria = brandingHex || null;
        } else {
          throw new Error('Cor inválida. Use o formato #RRGGBB.');
        }
      }

      if (Object.keys(body).length > 0) {
        const res = await apiFetch(`${API_BASE_URL}/api/v1/grupo-politico/${grupoId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Erro ao salvar cor');
        }
      }

      await refreshUserData();
      setBrandingSuccess('Identidade visual salva com sucesso!');
      setBrandingLogoFile(null);
    } catch (err) {
      setBrandingError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setBrandingLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <>
            <SectionTitle>Perfil</SectionTitle>
            <Card>
              <ProfileSection>
                <UserAvatar
                  name={userData?.nome || 'U'}
                  fotoUrl={userData?.foto_url}
                  size={80}
                  editable
                  onPhotoUpdated={refreshUserData}
                />
                <ProfileInfo>
                  <ProfileName>{userData?.nome || 'Usuário'}</ProfileName>
                  <ProfileEmail>{userData?.email_contato || 'email@exemplo.com'}</ProfileEmail>
                  {userData?.papeis && userData.papeis.length > 0 ? (
                    userData.papeis.map((papel) => (
                      <RoleBadge key={papel}>{papel}</RoleBadge>
                    ))
                  ) : (
                    <RoleBadge>{userData?.tipo_usuario === 'ADMIN' ? 'Administrador' : 'Assessor'}</RoleBadge>
                  )}
                </ProfileInfo>
              </ProfileSection>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z" />
                  </svg>
                  Segurança
                </CardTitle>
              </CardHeader>
              <InfoRow>
                <InfoLabel>Senha</InfoLabel>
                <CardButton $variant="secondary" onClick={() => setShowChangePasswordModal(true)}>Alterar</CardButton>
              </InfoRow>
              <InfoRow style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', marginTop: '16px' }}>
                <div>
                  <InfoLabel style={{ display: 'block', marginBottom: '4px', color: '#ef4444' }}>Sair da Conta</InfoLabel>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>Encerre sua sessão atual</span>
                </div>
                <CardButton $variant="danger" onClick={handleSignOut}>
                  Sair
                </CardButton>
              </InfoRow>
            </Card>
          </>
        );

      case 'regions':
        return (
          <>
            <SectionTitle>Regiões Políticas</SectionTitle>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <Card>
              <CardHeader>
                <CardTitle>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Minhas Regiões
                </CardTitle>
                <CardButton onClick={() => setShowCreateModal(true)}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" />
                  </svg>
                  Nova Região
                </CardButton>
              </CardHeader>
              
              {loadingRegioes ? (
                <EmptyState>
                  <p style={{ margin: '0', fontSize: '14px' }}>Carregando regiões...</p>
                </EmptyState>
              ) : regioes.length === 0 ? (
                <EmptyState>
                  <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>Nenhuma região cadastrada</p>
                  <p style={{ margin: '0', fontSize: '14px' }}>Clique em "Nova Região" para começar</p>
                </EmptyState>
              ) : (
                <div>
                  {regioes.map((regiao) => (
                    <RegionItem key={regiao.id}>
                      <RegionInfo>
                        <RegionName>{regiao.nome}</RegionName>
                        <RegionDetails>
                          {regiao.cidade_ids.length} {regiao.cidade_ids.length === 1 ? 'cidade' : 'cidades'}
                        </RegionDetails>
                      </RegionInfo>
                      <RegionActions>
                        <IconButton onClick={() => handleDeleteRegiao(regiao.id)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </RegionActions>
                    </RegionItem>
                  ))}
                </div>
              )}
            </Card>
          </>
        );

      case 'team':
        return (
          <>
            <SectionTitle>Time do Grupo</SectionTitle>
            <Card>
              <CardHeader>
                <CardTitle>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                  Time
                </CardTitle>
                {canManageUsers && (
                  <CardButton onClick={() => setIsInviteModalOpen(true)}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 4v16m8-8H4" />
                    </svg>
                    Adicionar Usuário
                  </CardButton>
                )}
              </CardHeader>

              <SearchAndViewContainer>
                <SearchBar
                  type="text"
                  placeholder={teamTab === 'members' ? "Buscar por nome, cargo ou e-mail..." : "Buscar convites..."}
                  value={teamSearchTerm}
                  onChange={(e) => setTeamSearchTerm(e.target.value)}
                />
                <ViewModeButtons>
                  <ViewModeButton 
                    active={teamViewMode === 'grid'} 
                    onClick={() => setTeamViewMode('grid')}
                    title="Visualização em Grade"
                  >
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </ViewModeButton>
                  <ViewModeButton 
                    active={teamViewMode === 'list'} 
                    onClick={() => setTeamViewMode('list')}
                    title="Visualização em Lista"
                  >
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </ViewModeButton>
                </ViewModeButtons>
              </SearchAndViewContainer>

              <TeamTabsContainer>
                <TeamTab active={teamTab === 'members'} onClick={() => setTeamTab('members')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Usuários Criados
                </TeamTab>
                {canManageUsers && (
                  <TeamTab active={teamTab === 'invites'} onClick={() => setTeamTab('invites')}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Convites Enviados
                  </TeamTab>
                )}
              </TeamTabsContainer>

              {teamTab === 'members' ? (
                loadingTeam ? (
                  <TeamEmptyState>
                    <EmptyStateText>Carregando...</EmptyStateText>
                  </TeamEmptyState>
                ) : filteredTeamMembers.length === 0 ? (
                  <TeamEmptyState>
                    <EmptyStateTitle>Nenhum membro encontrado</EmptyStateTitle>
                    <EmptyStateText>
                      {teamMembers.length === 0 ? 'Nenhum membro cadastrado ainda' : 'Tente ajustar sua busca'}
                    </EmptyStateText>
                  </TeamEmptyState>
                ) : teamViewMode === 'grid' ? (
                  <TeamGrid>
                    {filteredTeamMembers.map((member) => (
                      <MemberCard key={member.id}>
                        <PhotoWrapper>
                          <UserAvatar name={member.name} fotoUrl={member.foto_url} size={80} />
                        </PhotoWrapper>
                        <MemberName>{member.name}</MemberName>
                        <MemberRole>{member.role}</MemberRole>
                        <MemberEmail>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {member.email || 'Sem e-mail'}
                        </MemberEmail>
                      </MemberCard>
                    ))}
                  </TeamGrid>
                ) : (
                  <TeamList>
                    {filteredTeamMembers.map((member) => (
                      <MemberListItem key={member.id}>
                        <ListPhotoWrapper>
                          <UserAvatar name={member.name} fotoUrl={member.foto_url} size={60} />
                        </ListPhotoWrapper>
                        <ListMemberInfo>
                          <ListMemberHeader>
                            <ListMemberName>{member.name}</ListMemberName>
                            <ListMemberRole>{member.role}</ListMemberRole>
                          </ListMemberHeader>
                          <ListMemberEmail>
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {member.email || 'Sem e-mail'}
                          </ListMemberEmail>
                        </ListMemberInfo>
                      </MemberListItem>
                    ))}
                  </TeamList>
                )
              ) : (
                <>
                  {loadingInvites ? (
                    <TeamEmptyState>
                      <EmptyStateText>Carregando...</EmptyStateText>
                    </TeamEmptyState>
                  ) : pendingInvites.length === 0 ? (
                    <TeamEmptyState>
                      <EmptyStateTitle>Nenhum convite pendente</EmptyStateTitle>
                      <EmptyStateText>
                        Clique em "Adicionar Usuário" para criar um novo convite
                      </EmptyStateText>
                    </TeamEmptyState>
                  ) : (
                    <InvitesGrid>
                      {pendingInvites.map((invite) => (
                        <InviteCard key={invite.id}>
                          <InviteHeader>
                            <InviteInfo>
                              <InviteName>{invite.nome_assessor}</InviteName>
                              <InviteLevel level={invite.nivel_assessor}>
                                {invite.nivel_assessor}
                              </InviteLevel>
                            </InviteInfo>
                          </InviteHeader>
                          <InviteCode>{invite.codigo}</InviteCode>
                          <InviteExpiry>
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Válido até {formatDate(invite.valido_ate)}
                          </InviteExpiry>
                        </InviteCard>
                      ))}
                    </InvitesGrid>
                  )}
                </>
              )}
            </Card>
          </>
        );

      case 'branding':
        return (
          <>
            <SectionTitle>Identidade Visual</SectionTitle>
            <Card>
              <CardHeader>
                <CardTitle>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Cor Principal
                </CardTitle>
              </CardHeader>
              <div style={{ padding: '0 0 16px' }}>
                <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 16px', lineHeight: 1.6 }}>
                  Define a cor de destaque usada em toda a aplicação para os membros deste grupo.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <input
                    type="color"
                    value={brandingColor || '#1d4ed8'}
                    onChange={(e) => {
                      setBrandingColor(e.target.value);
                      setBrandingHex(e.target.value);
                    }}
                    style={{ width: 48, height: 40, border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', padding: 2 }}
                  />
                  <input
                    type="text"
                    value={brandingHex}
                    maxLength={7}
                    placeholder="#1d4ed8"
                    onChange={(e) => {
                      const val = e.target.value;
                      setBrandingHex(val);
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) setBrandingColor(val);
                    }}
                    style={{ width: 110, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'monospace' }}
                  />
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, background: /^#[0-9A-Fa-f]{6}$/.test(brandingColor) ? brandingColor : '#e2e8f0',
                    border: '1px solid #e2e8f0', flexShrink: 0
                  }} />
                </div>
              </div>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardHeader>
                <CardTitle>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Logo do Grupo
                </CardTitle>
              </CardHeader>
              <div style={{ padding: '0 0 16px' }}>
                <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 16px', lineHeight: 1.6 }}>
                  Exibido no topo da aplicação e na tela de login. Formatos aceitos: JPG, PNG, WebP, SVG (máx. 5 MB).
                </p>
                {(brandingLogoPreview ?? currentLogoUrl) && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>Prévia:</p>
                    <img
                      src={brandingLogoPreview ?? currentLogoUrl ?? ''}
                      alt="Logo prévia"
                      style={{ maxHeight: 64, maxWidth: 200, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, background: '#f8fafc' }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  id="branding-logo-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setBrandingLogoFile(file);
                      const url = URL.createObjectURL(file);
                      setBrandingLogoPreview(url);
                    }
                  }}
                />
                <label
                  htmlFor="branding-logo-input"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#374151' }}
                >
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {brandingLogoFile ? brandingLogoFile.name : 'Escolher arquivo'}
                </label>
              </div>
            </Card>

            {brandingError && <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14, color: '#dc2626' }}>{brandingError}</div>}
            {brandingSuccess && <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 14, color: '#16a34a' }}>{brandingSuccess}</div>}

            <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
              <CardButton
                onClick={handleSaveBranding}
                disabled={brandingLoading}
                style={{ opacity: brandingLoading ? 0.7 : 1 }}
              >
                {brandingLoading ? 'Salvando...' : 'Salvar Identidade Visual'}
              </CardButton>
              <button
                type="button"
                onClick={() => {
                  const color = userData?.grupo_cor_primaria ?? '';
                  setBrandingColor(color);
                  setBrandingHex(color);
                  setBrandingLogoPreview(userData?.grupo_logo_url ?? null);
                  setBrandingLogoFile(null);
                  setBrandingError('');
                  setBrandingSuccess('');
                }}
                style={{ padding: '10px 20px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#64748b' }}
              >
                Cancelar
              </button>
            </div>
          </>
        );

      case 'reports':
        return (
          <>
            <SectionTitle>Relatórios</SectionTitle>
            <Card>
              <CardHeader>
                <CardTitle>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Relatório de Cidade
                </CardTitle>
              </CardHeader>
              <div style={{ padding: '0 0 8px 0' }}>
                <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 20px 0', lineHeight: 1.6 }}>
                  Gere um relatório em PDF com informações detalhadas de um município: visão geral,
                  multiplicadores e demandas filtradas por período.
                </p>
                <CardButton
                  onClick={() => setIsReportModalOpen(true)}
                  style={{ display: 'inline-flex' }}
                >
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Novo Relatório
                </CardButton>
              </div>
            </Card>
          </>
        );
    }
  };

  return (
    <>
      <SettingsContainer>
        <Sidebar $hidden={mobileView === 'content'}>
        <SidebarHeader>
          <SidebarTitle>Configurações</SidebarTitle>
          <SidebarSubtitle>Gerencie sua conta</SidebarSubtitle>
        </SidebarHeader>

        <SidebarNav>
          <NavSection>
            <NavSectionTitle>Conta</NavSectionTitle>
            <NavItem $active={activeSection === 'profile'} onClick={() => { setActiveSection('profile'); setMobileView('content'); }}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
              </svg>
              <span>Perfil</span>
            </NavItem>
          </NavSection>

          {(isAdmin || canManageRegions || canManageUsers || canManageBranding) && (
            <NavSection>
              <NavSectionTitle>Grupo Político</NavSectionTitle>
              {(isAdmin || canManageRegions) && (
                <NavItem $active={activeSection === 'regions'} onClick={() => { setActiveSection('regions'); setMobileView('content'); }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span>Regiões</span>
                </NavItem>
              )}
              {canManageUsers && (
                <NavItem $active={activeSection === 'team'} onClick={() => { setActiveSection('team'); setMobileView('content'); }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                  <span>Time</span>
                </NavItem>
              )}
              {(isAdmin || canManageBranding) && (
                <NavItem $active={activeSection === 'branding'} onClick={() => { setActiveSection('branding'); setMobileView('content'); }}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  <span>Identidade Visual</span>
                </NavItem>
              )}
            </NavSection>
          )}
          
          {(isAdmin || can('roles.manage')) && (
            <NavSection>
              <NavSectionTitle>Segurança</NavSectionTitle>
              <NavItem onClick={() => navigate('/papeis')}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Papéis e Permissões</span>
              </NavItem>
            </NavSection>
          )}

          {canAccessReports && (
            <NavSection>
              <NavSectionTitle>Ferramentas</NavSectionTitle>
              <NavItem $active={activeSection === 'reports'} onClick={() => { setActiveSection('reports'); setMobileView('content'); }}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Relatórios</span>
              </NavItem>
            </NavSection>
          )}
        </SidebarNav>
      </Sidebar>

      <MainContent $hidden={mobileView === 'menu'}>
        <MobileBackHeader>
          <MobileBackButton onClick={() => setMobileView('menu')}>
            <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Configurações
          </MobileBackButton>
        </MobileBackHeader>
        <MobileContentArea>
          {renderContent()}
        </MobileContentArea>
      </MainContent>
    </SettingsContainer>

    {showCreateModal && (
      <SideSheetOverlay onClick={() => setShowCreateModal(false)}>
        <SideSheetContent onClick={(e) => e.stopPropagation()}>
          <SideSheetHeader>
            <SideSheetTitle>Nova Região Política</SideSheetTitle>
            <CloseButton onClick={() => setShowCreateModal(false)}>
              <X />
            </CloseButton>
          </SideSheetHeader>

          <SideSheetForm onSubmit={handleCreateRegiao}>
            <FormGroup>
              <FormLabel htmlFor="nomeRegiao">Nome da Região</FormLabel>
              <FormInput
                id="nomeRegiao"
                type="text"
                placeholder="Ex: Região Norte, Zona Sul, etc."
                value={nomeRegiao}
                onChange={(e) => setNomeRegiao(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Cidades da Região</FormLabel>
              <CityMultiSelect
                selectedCityIds={cidadesSelecionadas}
                onChange={setCidadesSelecionadas}
                placeholder="Buscar e selecionar cidades..."
              />
            </FormGroup>

            <FormActions>
              <SideSheetButton type="button" $variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </SideSheetButton>
              <SideSheetButton 
                type="submit" 
                $variant="primary"
                disabled={creatingRegiao || !nomeRegiao.trim() || cidadesSelecionadas.length === 0}
              >
                {creatingRegiao ? 'Criando...' : 'Criar Região'}
              </SideSheetButton>
            </FormActions>
          </SideSheetForm>
        </SideSheetContent>
      </SideSheetOverlay>
    )}

    <InviteModal
      isOpen={isInviteModalOpen}
      onClose={() => setIsInviteModalOpen(false)}
      onSuccess={handleInviteSuccess}
    />

    <CityReportModal
      isOpen={isReportModalOpen}
      onClose={() => setIsReportModalOpen(false)}
    />

    {/* Change password / password recovery modal */}
    {(showChangePasswordModal || isPasswordRecovery) && !passwordUpdateSuccess && (
      <SideSheetOverlay onClick={isPasswordRecovery ? undefined : handleClosePasswordModal}>
        <SideSheetContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <SideSheetHeader>
            <SideSheetTitle>{isPasswordRecovery ? 'Redefinir Senha' : 'Alterar Senha'}</SideSheetTitle>
            {!isPasswordRecovery && (
              <button
                type="button"
                onClick={handleClosePasswordModal}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#94a3b8', display: 'flex', alignItems: 'center', padding: 4,
                }}
              >
                <X size={20} />
              </button>
            )}
          </SideSheetHeader>
          <SideSheetForm onSubmit={handlePasswordUpdate}>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#64748b' }}>
              {isPasswordRecovery
                ? 'Escolha uma nova senha para sua conta.'
                : 'Digite sua nova senha. Ela deve ter pelo menos 6 caracteres.'}
            </p>
            {passwordUpdateError && (
              <ErrorMessage>{passwordUpdateError}</ErrorMessage>
            )}
            <FormGroup>
              <FormLabel htmlFor="newPassword">Nova Senha</FormLabel>
              <FormInput
                id="newPassword"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="confirmNewPassword">Confirmar Nova Senha</FormLabel>
              <FormInput
                id="confirmNewPassword"
                type="password"
                placeholder="Repita a nova senha"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
            </FormGroup>
            <FormActions>
              {!isPasswordRecovery && (
                <SideSheetButton
                  type="button"
                  $variant="secondary"
                  onClick={handleClosePasswordModal}
                >
                  Cancelar
                </SideSheetButton>
              )}
              <SideSheetButton
                type="submit"
                $variant="primary"
                disabled={updatingPassword || !newPassword || !confirmNewPassword}
              >
                {updatingPassword ? 'Salvando...' : 'Salvar nova senha'}
              </SideSheetButton>
            </FormActions>
          </SideSheetForm>
        </SideSheetContent>
      </SideSheetOverlay>
    )}

    {/* Success confirmation after password update */}
    {passwordUpdateSuccess && (
      <SideSheetOverlay onClick={() => {}}>
        <SideSheetContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <SideSheetHeader>
            <SideSheetTitle>Senha atualizada</SideSheetTitle>
          </SideSheetHeader>
          <div style={{ padding: '24px 32px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <SuccessMessage>Sua senha foi redefinida com sucesso. Já pode usar a nova senha no próximo acesso.</SuccessMessage>
            <SideSheetButton
              type="button"
              $variant="primary"
              onClick={() => {
                setPasswordUpdateSuccess(false);
                setShowChangePasswordModal(false);
              }}
            >
              Continuar
            </SideSheetButton>
          </div>
        </SideSheetContent>
      </SideSheetOverlay>
    )}
    </>
  );
};

export default Settings;
